import type { DjangoPaginatedResults } from '@backmarket/http-api'

import { createHttpEndpoint } from '../../utils'

import type {
  BuybackOrderDetails,
  BuybackOrderDetailsCounterOfferReason,
  BuybackOrderDetailsOnHoldAction,
  BuybackOrderDetailsSuspensionReasons,
  BuybackOrderDetailsValidateOrder,
  GetRefurbisherFees,
  GetRefurbisherInsights,
  GetRefurbisherPaymentMethodsConfig,
  MerchantMarkets,
  MerchantOrder,
  PostRefurbisherPaymentTokenizationResponse,
  RefurbisherCounterOfferDetails,
  RefurbisherProduct,
  RefurbisherQuotations,
  RefurbisherQuotationsListing,
} from './refurbisher.types'

/**
 * Get merchant markets
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-sourcing-merchant}
 */
export const getMerchant = createHttpEndpoint<MerchantMarkets>({
  method: 'GET',
  operationId: 'getSourcingMerchantInfos',
  path: '/bm/sourcing/merchant',
})

/**
 * Get all BuyBack orders
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-buyback-merchant-orders}
 */
export const getMerchantOrders = createHttpEndpoint<
  DjangoPaginatedResults<MerchantOrder>
>({
  method: 'GET',
  operationId: 'getSourcingOrders',
  path: '/bm/sourcing/merchant/orders',
})

export const exportSourcingOrders = createHttpEndpoint<BlobPart>({
  method: 'GET',
  operationId: 'exportSourcingOrders',
  path: '/bm/buyback/v1/refurbisher/orders/export',
})

/**
 * Export BuyBack orders
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-merchant-buyback-orders-export}
 */
export const postMerchantOrdersExport = createHttpEndpoint({
  method: 'GET',
  operationId: 'exportSourcingOrders',
  path: '/bm/sourcing/merchant/orders/export',
})

/**
 * BuyBack orders pending reply
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-sourcing-merchant-orders-pending-reply}
 */
export const getMerchantOrderPendingReply = createHttpEndpoint<
  DjangoPaginatedResults<MerchantOrder>
>({
  method: 'GET',
  operationId: 'getSourcingPending',
  path: '/bm/sourcing/merchant/orders/pending_reply',
})

/**
 * Retrieves all the received orders of an authenticated merchant
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-sourcing-merchant-orders-received}
 */
export const getMerchantOrdersReceived = createHttpEndpoint<
  DjangoPaginatedResults<MerchantOrder>
>({
  method: 'GET',
  operationId: 'getSourcingReceived',
  path: '/bm/sourcing/merchant/orders/received',
})

/**
 * Buyback order details
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/buybackRefurbisherOrderDetails}
 */
export const getRefurbisherOrder = createHttpEndpoint<BuybackOrderDetails>({
  method: 'GET',
  operationId: 'getCustomerBuybackOrderDetails',
  path: '/bm/buyback/v1/refurbisher/orders/:id',
})

/**
 * Buyback Order Details On Hold Actions
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/buybackRefurbisherOrderOnHoldActions}
 */
export const getRefurbisherOrderOnHoldActions = createHttpEndpoint<
  BuybackOrderDetailsOnHoldAction[]
>({
  method: 'GET',
  operationId: 'getBuybackOrderOnHoldActions',
  path: '/bm/buyback/v1/refurbisher/orders/:id/on-hold-actions',
})

/**
 * Validate BuyBack order
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/put-bm-buyback-v1-orders-validate
 */
export const putRefurbisherOrderValidate =
  createHttpEndpoint<BuybackOrderDetailsValidateOrder>({
    method: 'PUT',
    operationId: 'setCustomerOrderValidated',
    path: '/bm/buyback/v1/refurbisher/orders/:id/validate-order',
  })

/**
 * Buyback order counter offer details
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-buyback-refurbisher-counteroffer}
 */
export const getRefurbisherCounterOfferDetails =
  createHttpEndpoint<RefurbisherCounterOfferDetails>({
    method: 'GET',
    operationId: 'getRefurbisherCounterOfferDetails',
    path: '/bm/buyback/v1/refurbisher/orders/:id/counter-offer/last',
  })

/**
 * Buyback counter offer possible reasons
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-refurbisher-counter-offer-reason}
 */
export const getRefurbisherOrderCounterOffersReasons = createHttpEndpoint<
  BuybackOrderDetailsCounterOfferReason[]
>({
  method: 'GET',
  operationId: 'getBuyBackOrderCounterOfferReasons',
  path: '/bm/buyback/v1/refurbisher/orders/:id/counter-offers/reasons',
})

/**
 * Set a buyback counter offer
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/put-bm-refurbisher-counter-offer}
 */
export const putRefurbisherOrderCounterOffer = createHttpEndpoint({
  method: 'PUT',
  operationId: 'setBuyBackOrderCounterOffer',
  path: '/bm/buyback/v1/refurbisher/orders/:id/counter-offers',
})

/**
 * Suspend a buyback order
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/put-bm-refurbisher-suspensions}
 */
export const putRefurbisherOrderSuspension = createHttpEndpoint({
  method: 'PUT',
  operationId: 'postBuybackOrderSuspendReasons',
  path: '/bm/buyback/v1/refurbisher/orders/:id/suspensions',
})

/**
 * Buyback order suspension possible reasons
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-refurbisher-suspensions-reasons}
 */
export const getRefurbisherSuspendReasons =
  createHttpEndpoint<BuybackOrderDetailsSuspensionReasons>({
    method: 'GET',
    operationId: 'getBuybackOrderSuspendReasons',
    path: '/bm/buyback/v1/refurbisher/suspensions/:suspendCategory/reasons',
  })

/**
 * Get product
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-refurbisher-buyback-product}
 */
export const getRefurbisherProduct = createHttpEndpoint<RefurbisherProduct>({
  method: 'GET',
  operationId: 'getRefurbisherProductInfo',
  path: '/bm/buyback/v1/refurbisher/products/:productPublicId',
})

/**
 * Get buyback listings for a given product and market.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-refurbisher-buyback-listings}
 */
export const getRefurbisherProductListings = createHttpEndpoint<
  RefurbisherQuotationsListing[]
>({
  method: 'GET',
  operationId: 'getRefurbisherProductQuotations',
  path: '/bm/buyback/v1/refurbisher/products/:productPublicId/markets/:countryCode/listings',
})

/**
 * Get refurbisher's quotations
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-refurbisher-quotations}
 */
export const getRefurbisherQuotations =
  createHttpEndpoint<RefurbisherQuotations>({
    method: 'GET',
    operationId: 'getRefurbisherQuotations',
    path: '/bm/buyback/v1/refurbisher/quotations',
  })

/**
 * put-bm-refurbisher-buyback-quotation
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/put-bm-refurbisher-buyback-quotation}
 */
export const putRefurbisherQuotations = createHttpEndpoint({
  method: 'PUT',
  operationId: 'updateRefurbisherQuotation',
  path: '/bm/buyback/v1/refurbisher/quotations',
})

/**
 * Get refurbisher's insights
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#tag/Refurbisher/operation/get-bm-refurbisher-quotations}
 */
export const getRefurbisherInsights =
  createHttpEndpoint<GetRefurbisherInsights>({
    method: 'GET',
    operationId: 'getRefurbisherInsights',
    path: 'buyback/v1/refurbisher/insights',
  })

/**
 * Get refurbisher's payment methods configuration
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#/Refurbisher/get-refurbisher-payment-methods}
 */
export const getRefurbisherPaymentMethodsConfig =
  createHttpEndpoint<GetRefurbisherPaymentMethodsConfig>({
    method: 'GET',
    operationId: 'getRefurbisherPaymentMethodsConfig',
    path: '/buyback/v1/refurbisher/payments/payment-methods',
  })

/**
 * Put refurbisher's bank transfer preference
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#/Refurbisher/put-refurbisher-bank-transfer-preference}
 */
export const putBankTransferPreference = createHttpEndpoint({
  method: 'PUT',
  operationId: 'putBankTransferPreference',
  path: '/buyback/v1/refurbisher/payments/bank-transfer-preference',
})

/**
 * Get refurbisher's fees
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-shared-api/definition#/Refurbisher/getRefurbisherFees}
 */
export const getRefurbisherFees = createHttpEndpoint<GetRefurbisherFees>({
  method: 'GET',
  operationId: 'getRefurbisherFees',
  path: '/buyback/v1/refurbisher/fees',
})

/**
 * Put refurbisher's fees
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-shared-api/definition#/Refurbisher/putRefurbisherFees}
 */
export const putRefurbisherFees = createHttpEndpoint({
  method: 'PUT',
  operationId: 'putRefurbisherFees',
  path: '/buyback/v1/refurbisher/fees',
})

/**
 * Post refurbisher's payment tokenization
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/buyback-bm-api/definition#/Refurbisher/post-refurbisher-payment-tokenization}
 */
export const postRefurbisherPaymentTokenization =
  createHttpEndpoint<PostRefurbisherPaymentTokenizationResponse>({
    method: 'POST',
    operationId: 'postRefurbisherPaymentTokenization',
    path: '/buyback/v1/refurbisher/payments/tokenization',
  })
